import { ChangeDetectionStrategy, Component, ContentChild, ElementRef, HostBinding, HostListener, Input, TemplateRef, ViewChild } from '@angular/core';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { PopoverController } from 'providers/popover-controller';
import { BackdropMode } from 'components/popover-host/popover-host';
import { CsTooltipPopover } from './cs-tooltip-popover';
import { BackdropController } from 'providers/backdrop-controller';
import { PubSubService, navigationStackChangedEvent, PubSubRegistration } from 'services/pubsub.service';
import { HelpLinkComponent } from 'components/help-link/help-link';
import { NavController } from 'providers/nav-controller';
import { IHelpCardPage } from 'pages/help-card-page/help-card-page-interface';
import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';

type TooltipPosition = 'right' | 'bottom' | 'left' | 'top';

@Component({
  selector: 'cs-tooltip',
  templateUrl: 'cs-tooltip.html',
  standalone: false,
  host: { 'class': 'tooltip' },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsTooltipComponent {

  @Input() text?: TranslatedString;
  @Input() title?: TranslatedString;
  @Input() iconType: 'help' | 'info' | 'warning' | 'time' = 'info';
  @Input() buttonText?: TranslatedString;  // Only shown in mobile popover
  @Input() buttonCallback: () => void;
  @Input() mobileText?: TranslatedString;  // Override text for mobile popover only (allows ng-content on desktop)
  @Input() hasCustomTrigger: boolean = false;  // Set to true when using [tooltip-trigger] content projection

  @ViewChild('tooltip', { static: true }) tooltipElement: ElementRef;
  @ViewChild('button', { static: true }) buttonElement: ElementRef;
  @ViewChild('contentTemplate', { static: false, read: TemplateRef }) contentTemplate: TemplateRef<any>;
  @ContentChild(HelpLinkComponent) private projectedHelpLink: HelpLinkComponent;

  @HostBinding('class.position-right') get isPositionRight() { return this.tooltipPosition === 'right'; }
  @HostBinding('class.position-bottom') get isPositionBottom() { return this.tooltipPosition === 'bottom'; }
  @HostBinding('class.position-left') get isPositionLeft() { return this.tooltipPosition === 'left'; }
  @HostBinding('class.position-top') get isPositionTop() { return this.tooltipPosition === 'top'; }

  tooltipPosition: TooltipPosition = 'bottom';
  caretOffset: string = '50%'; // Position of caret along the edge (either left/right for top/bottom position, or top/bottom for left/right position)

  private navSubscription: PubSubRegistration;

  constructor(
    private elementRef: ElementRef,
    private popoverCtrl: PopoverController,
    private backdropCtrl: BackdropController,
    private pubSub: PubSubService,
    private navCtrl: NavController,
  ) {

  }

  resizeObserver = new ResizeObserver(() => void this.onResize());

  tooltipTextElementEntered = false;
  hideTooltipTimer: any = null;

  // Add listener for hover
  @HostListener('mouseenter')
  onMouseEnter() {
    // on touch devices, we don't show tooltip on hover
    if (this.isTouchDevice()) {
      return;
    }
    // Clear any pending hide timer
    if (this.hideTooltipTimer) {
      clearTimeout(this.hideTooltipTimer);
      this.hideTooltipTimer = null;
    }

    // Reset the flag when entering the component
    this.tooltipTextElementEntered = false;

    let animation = () => {
      void this.onResize();

      // if tooltip is not visible, we don't need to continue the animation
      let tooltipElement = this.tooltipElement.nativeElement as HTMLElement;
      if (tooltipElement.style.visibility === 'visible') {
        requestAnimationFrame(animation);
      }
    }

    if (this.tooltipElement) {
      const tooltipElement = this.tooltipElement.nativeElement as HTMLElement;
      requestAnimationFrame(animation);
      tooltipElement.style.visibility = 'visible';
    }
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    // on touch devices, we don't show tooltip on hover
    if (this.isTouchDevice()) {
      return;
    }
    if (this.tooltipElement) {
      const tooltipElement = this.tooltipElement.nativeElement as HTMLElement;
      // Use a longer delay to allow time to move to the tooltip
      this.hideTooltipTimer = setTimeout(() => {
        if (!this.tooltipTextElementEntered) {
          tooltipElement.style.visibility = 'hidden';
        }
      }, 100);  // Increased from 100ms to 200ms for better UX
    }
  }

  async onResize() {
    if (this.tooltipElement && this.buttonElement) {
      const tooltipElement = this.tooltipElement.nativeElement as HTMLElement;
      const buttonElement = this.buttonElement.nativeElement as HTMLElement;

      const buttonRect = buttonElement.getBoundingClientRect();
      const tooltipRect = tooltipElement.getBoundingClientRect();

      const spacing = 8; // Space between button and tooltip
      const margin = 8; // Minimum margin from window edge

      // determine best position with priority: right > bottom > left > top
      let position: TooltipPosition;
      let top: number;
      let left: number;

      // try right
      if (buttonRect.right + spacing + tooltipRect.width + margin <= window.innerWidth) {
        position = 'right';
        left = buttonRect.right + spacing;
        top = buttonRect.top + (buttonRect.height / 2) - (tooltipRect.height / 2);
        // adjust top if it goes outside viewport
        if (top < margin) {
          top = margin;
        } else if (top + tooltipRect.height + margin > window.innerHeight) {
          top = window.innerHeight - tooltipRect.height - margin;
        }
      }
      // try bottom
      else if (buttonRect.bottom + spacing + tooltipRect.height + margin <= window.innerHeight) {
        position = 'bottom';
        left = buttonRect.left + (buttonRect.width / 2) - (tooltipRect.width / 2);
        top = buttonRect.bottom + spacing;
        // adjust left if it goes outside viewport
        if (left < margin) {
          left = margin;
        } else if (left + tooltipRect.width + margin > window.innerWidth) {
          left = window.innerWidth - tooltipRect.width - margin;
        }
      }
      // try left
      else if (buttonRect.left - spacing - tooltipRect.width >= margin) {
        position = 'left';
        left = buttonRect.left - spacing - tooltipRect.width;
        top = buttonRect.top + (buttonRect.height / 2) - (tooltipRect.height / 2);
        // adjust top if it goes outside viewport
        if (top < margin) {
          top = margin;
        } else if (top + tooltipRect.height + margin > window.innerHeight) {
          top = window.innerHeight - tooltipRect.height - margin;
        }
      }
      // try top
      else if (buttonRect.top - spacing - tooltipRect.height >= margin) {
        position = 'top';
        left = buttonRect.left + (buttonRect.width / 2) - (tooltipRect.width / 2);
        top = buttonRect.top - spacing - tooltipRect.height;
        // adjust left if it goes outside viewport
        if (left < margin) {
          left = margin;
        } else if (left + tooltipRect.width + margin > window.innerWidth) {
          left = window.innerWidth - tooltipRect.width - margin;
        }
      }
      // default to bottom if nothing fits perfectly
      else {
        position = 'bottom';
        left = buttonRect.left + (buttonRect.width / 2) - (tooltipRect.width / 2);
        top = buttonRect.bottom + spacing;
        // adjust to fit in viewport
        if (left < margin) {
          left = margin;
        } else if (left + tooltipRect.width + margin > window.innerWidth) {
          left = window.innerWidth - tooltipRect.width - margin;
        }
        if (top + tooltipRect.height + margin > window.innerHeight) {
          top = window.innerHeight - tooltipRect.height - margin;
        }
      }

      this.tooltipPosition = position;
      tooltipElement.style.left = left + 'px';
      tooltipElement.style.top = top + 'px';

      // remove all position classes and add the current one
      tooltipElement.classList.remove('position-right', 'position-bottom', 'position-left', 'position-top');
      tooltipElement.classList.add(`position-${position}`);

      // calculate caret offset to point at button center
      const buttonCenterX = buttonRect.left + buttonRect.width / 2;
      const buttonCenterY = buttonRect.top + buttonRect.height / 2;

      if (position === 'right' || position === 'left') {
        // for horizontal positions, caret offset is vertical (from tooltip top to button center)
        const caretTop = buttonCenterY - top;
        this.caretOffset = caretTop + 'px';
      } else {
        // for vertical positions, caret offset is horizontal (from tooltip left to button center)
        const caretLeft = buttonCenterX - left;
        this.caretOffset = caretLeft + 'px';
      }

      // apply caret offset as CSS custom property
      tooltipElement.style.setProperty('--caret-offset', this.caretOffset);
    }
  }

  // Handle touch/click events for touch devices
  @HostListener('click', ['$event'])
  onTouch(event: Event) {
    // always stop propagation to prevent parent click handlers (e.g., menu items)
    event.stopPropagation();
    event.preventDefault();

    // only show tooltip on touch devices (desktop uses hover)
    if (!this.isTouchDevice()) {
      return;
    }

    // On small screens, show as bottom sheet
    if (hasClientSmallScreen()) {
      let tooltipContent = this.mobileText || this.text || null;
      let tooltipTemplate = !this.mobileText && !this.text && this.contentTemplate ? this.contentTemplate : null;
      let buttonText = this.buttonText || null;
      let buttonCallback = this.buttonCallback || null;

      // if no content available via inputs, extract from rendered ng-content
      if (!tooltipContent && !tooltipTemplate) {
        // the projected content was already rendered (and translated) elsewhere; this just reads its text
        const extracted = this.extractTooltipText();
        tooltipContent = extracted === null ? null : untranslatedString(extracted);

        // if there's a projected help-link, wire it as the popover button
        if (this.projectedHelpLink?.helpId && !buttonText) {
          const helpId = this.projectedHelpLink.helpId;
          buttonText = LL.common.buttons.learnMore();
          buttonCallback = () => {
            void this.navCtrl.pushAsync(IHelpCardPage, { helpId });
          };
        }
      }

      void this.popoverCtrl.present(
        CsTooltipPopover,
        { tooltipContent, tooltipTemplate, tooltipTitle: this.title || null, buttonText, buttonCallback },
        {
          targetFromElement: this.elementRef.nativeElement as HTMLElement,
          bottomSheet: true,
          animate: true,
          overlayPreviousPopover: true,
          backdropMode: BackdropMode.Visible,
        }
      );
    } else {
      // On larger touch screens (tablets), show the regular tooltip with backdrop
      this.showTooltip();

      // Show invisible backdrop to capture outside clicks
      this.backdropCtrl.showSimpleBackdrop({
        onClick: () => {
          this.hideTooltip();
          this.backdropCtrl.hideSimpleBackdrop();
        },
        disableDismissOnClick: false,
        invisible: true
      });
    }
  }

  private extractTooltipText(): string | null {
    if (!this.tooltipElement) return null;
    const el = this.tooltipElement.nativeElement as HTMLElement;
    const clone = el.cloneNode(true) as HTMLElement;
    clone.querySelector('.tooltip-caret')?.remove();
    // strip every projected help-link, not just the first — a tooltip may
    // carry more than one, and any leftover would leak its link text into
    // the extracted body
    clone.querySelectorAll('help-link').forEach(link => link.remove());
    return clone.textContent?.trim() || null;
  }

  private isTouchDevice() {
    return ('ontouchstart' in document.documentElement);
  }

  private showTooltip() {
    // Clear any pending hide timer
    if (this.hideTooltipTimer) {
      clearTimeout(this.hideTooltipTimer);
      this.hideTooltipTimer = null;
    }

    // Reset the flag when showing the tooltip
    this.tooltipTextElementEntered = false;

    let animation = () => {
      void this.onResize();

      // if tooltip is not visible, we don't need to continue the animation
      let tooltipElement = this.tooltipElement.nativeElement as HTMLElement;
      if (tooltipElement.style.visibility === 'visible') {
        requestAnimationFrame(animation);
      }
    }

    if (this.tooltipElement) {
      const tooltipElement = this.tooltipElement.nativeElement as HTMLElement;
      requestAnimationFrame(animation);
      tooltipElement.style.visibility = 'visible';
    }
  }

  private hideTooltip() {
    if (this.tooltipElement) {
      const tooltipElement = this.tooltipElement.nativeElement as HTMLElement;
      tooltipElement.style.visibility = 'hidden';
    }
  }

  ngOnDestroy(): void {
    // Clean up backdrop if it's still showing
    this.backdropCtrl.hideSimpleBackdrop();

    // unsubscribe from navigation events
    if (this.navSubscription) {
      this.navSubscription.unsubscribe();
    }

    // Call the existing destroy logic
    this.resizeObserver.disconnect();

    // Clear any pending hide timer
    if (this.hideTooltipTimer) {
      clearTimeout(this.hideTooltipTimer);
      this.hideTooltipTimer = null;
    }

    if (this.tooltipElement) {
      const tooltipElement = this.tooltipElement.nativeElement as HTMLElement;
      const parentElement = tooltipElement.parentElement as HTMLElement;
      if (parentElement) {
        parentElement.removeChild(tooltipElement);
      }
    }
  }

  ngOnInit() {
    // hide tooltip when navigation changes
    this.navSubscription = this.pubSub.subscribe(navigationStackChangedEvent, () => {
      this.hideTooltip();
    });
  }

  ngAfterViewInit(): void {

    // reparent tooltip element to the body to avoid overflow issues
    if (this.tooltipElement) {
      const tooltipElement = this.tooltipElement.nativeElement as HTMLElement;
      tooltipElement.addEventListener('mouseleave', () => {
        // Start the same hide timer as when leaving the main element
        this.hideTooltipTimer = setTimeout(() => {
          tooltipElement.style.visibility = 'hidden';
          this.tooltipTextElementEntered = false;
        }, 100);
      });
      tooltipElement.addEventListener('mouseenter', () => {
        // Clear any pending hide timer when entering the tooltip
        if (this.hideTooltipTimer) {
          clearTimeout(this.hideTooltipTimer);
          this.hideTooltipTimer = null;
        }
        this.tooltipTextElementEntered = true;
      });

      const parentElement = this.elementRef.nativeElement as HTMLElement;
      parentElement.removeChild(tooltipElement);
      document.body.appendChild(tooltipElement);
    }

    this.resizeObserver.observe(document.body);
    this.resizeObserver.observe(this.elementRef.nativeElement);

    void this.onResize();

    const element = this.elementRef.nativeElement as HTMLElement;
    const parentElement = element.parentElement;
    if (parentElement) {
      parentElement.style.overflow = 'visible'; // Ensure parent allows overflow for tooltip visibility
    }

    //document.body.addEventListener('scroll', this.onResize.bind(this), { capture: true });
  }


}